import React, { useState } from 'react';
import styles from './ServiceContactForm.module.css';
import TurnstileWidget, { TURNSTILE_ACTION } from '../TurnstileWidget';

const ServiceContactForm = ({ serviceName, serviceType = 'Service Inquiry' }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);

  const resetTurnstile = () => {
    setTurnstileToken('');
    setTurnstileResetSignal((value) => value + 1);
  };

  const getErrorMessage = (data, fallback) => {
    if (data.errors && data.errors.length > 0) {
      const firstError = data.errors[0];
      return typeof firstError === 'string' ? firstError : firstError.msg;
    }

    return data.message || fallback;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear status message when user starts typing
    if (submitStatus.message) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the security check before sending your request.'
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Prepare data for PHP backend
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: `Service Request: ${serviceName}\nType: ${serviceType}\n\nPlease contact me regarding this service.`,
        turnstileToken,
        turnstileAction: TURNSTILE_ACTION,
      };

      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! We will contact you within 24 hours.'
        });
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: getErrorMessage(data, 'Failed to submit request. Please try again.')
        });
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Unable to connect. Please call us at (818) 949-1031.'
      });
    } finally {
      resetTurnstile();
      setIsSubmitting(false);
    }
  };

  return (
    <form className={styles.contactForm} onSubmit={handleSubmit}>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Name</label>
        <input
          type="text"
          name="name"
          placeholder="John Carter"
          className={styles.formInput}
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Phone</label>
        <input
          type="tel"
          name="phone"
          placeholder="(123) 456 - 789"
          className={styles.formInput}
          value={formData.phone}
          onChange={handleChange}
          required
        />
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Email</label>
        <input
          type="email"
          name="email"
          placeholder="example@email.com"
          className={styles.formInput}
          value={formData.email}
          onChange={handleChange}
          required
        />
      </div>

      {submitStatus.message && (
        <div className={`${styles.statusMessage} ${styles[submitStatus.type]}`}>
          {submitStatus.message}
        </div>
      )}

      <div className={styles.turnstileField}>
        <TurnstileWidget
          resetSignal={turnstileResetSignal}
          onVerify={setTurnstileToken}
          onExpire={() => setTurnstileToken('')}
          onError={() => setTurnstileToken('')}
        />
      </div>

      <button type="submit" className={styles.submitButton} disabled={isSubmitting || !turnstileToken}>
        {isSubmitting ? 'Sending...' : 'Contact us'}
      </button>
    </form>
  );
};

export default ServiceContactForm;
